import { logError } from '@databases/logs';
import { runDatabaseAiField } from '@databases/rovo-bridge';
import type { AiSummaryFieldConfig } from '@databases/types/database';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  type AiSummaryFieldRunOperation,
  buildAiSummaryPrompt
} from './ai-summary-prompts';

export { hasUserEditedAiSummaryCells } from './has-user-edited-ai-summary-cells';

type RunAiSummaryFieldBaseOptions = {
  contentId: string;
  fieldId: string;
  targetFieldName: string;
  config: AiSummaryFieldConfig;
  locale: string;
};

export type RunAiSummaryFieldOptions = RunAiSummaryFieldBaseOptions &
  (
    | {
        operation: Exclude<AiSummaryFieldRunOperation, 'single-entry-generate'>;
      }
    | {
        operation: 'single-entry-generate';
        entryId: string;
      }
  );

export async function runAiSummaryField(
  options: RunAiSummaryFieldOptions
): Promise<void> {
  const isEnabled = getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
    fireExperimentExposure: false
  });
  if (!isEnabled) {
    return;
  }

  try {
    const request = {
      contentId: options.contentId,
      locale: options.locale,
      prompt: buildAiSummaryPrompt({
        operation: options.operation,
        targetFieldName: options.targetFieldName,
        config: options.config
      })
    };

    if (options.operation === 'single-entry-generate') {
      await runDatabaseAiField({
        ...request,
        target: {
          aiFieldType: 'summary',
          fieldId: options.fieldId,
          scope: 'entry',
          entryId: options.entryId
        }
      });
      return;
    }

    await runDatabaseAiField({
      ...request,
      contextScope:
        options.operation === 'refresh-empty-entries'
          ? { kind: 'empty-target-field-entries' }
          : { kind: 'all-entries' },
      target: {
        aiFieldType: 'summary',
        fieldId: options.fieldId,
        scope: 'field'
      }
    });
  } catch (error) {
    logError(
      'Failed to generate AI Summary field',
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }
}
